import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Button,
  FormControl,
  FormHelperText,
  PinInput,
  Stack,
  Typography,
  Alert,
} from "@financeable-com-au/financeable-ui";
import { AuthCard } from "@components/AuthCard";
import { AUTH_REQUEST_KEYS, authV2EmailVerify, authV2Selector, toAuthV2Error } from "@/store/slices/authV2Slice";
import type { AppDispatch } from "@/store/store";
import { openSnackbar } from "@/store/slices/snackbarSlice";

const AuthVerifyEmail = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";
  const userId = searchParams.get("userId") ?? "";
  const authV2 = useSelector(authV2Selector);
  const isLoading = Boolean(authV2?.loading?.[AUTH_REQUEST_KEYS.emailVerify]);

  const [isSuccess, setSuccess] = useState(false);

  const verifyEmail = async (nextUserId: string, nextCode: string[] | string) => {
    const codeString = Array.isArray(nextCode) ? nextCode.join("") : nextCode;

    const result = await dispatch(
      authV2EmailVerify({
        userId: nextUserId.trim(),
        code: codeString.trim(),
      }),
    );

    if (result.error) {
      const parsedError = toAuthV2Error(result.payload);
      dispatch(
        openSnackbar({
          message: parsedError.message,
          intent: "danger",
        }),
      );
      return;
    }

    setSuccess(true);
    dispatch(
      openSnackbar({
        message: "Email verified successfully.",
        intent: "success",
      }),
    );
  };

  const formik = useFormik({
    initialValues: {
      code: Array(6).fill("") as string[],
    },
    onSubmit: (values) => {
      verifyEmail(userId, values.code);
    },
    validationSchema: yup.object({
      code: yup
        .array()
        .of(yup.string())
        .min(6)
        .test(
          "all-filled",
          "Please enter the full 6-digit code.",
          (value) =>
            Array.isArray(value) && value.length === 6 && value.every((v) => typeof v === "string" && v.trim() !== ""),
        ),
    }),
    validateOnChange: false,
    validateOnBlur: false,
  });

  const navigateToSignIn = () => {
    navigate("/auth/sign-in");
  };

  return (
    <>
      <Stack textAlign="center" rowGap={1}>
        <Typography level="title-lg">Verify your email</Typography>
        <Typography level="body-md">Enter your verification code to activate your account.</Typography>
      </Stack>
      <AuthCard onSubmit={() => formik.handleSubmit()}>
        <Stack rowGap={3}>
          {isSuccess ? (
            <Stack rowGap={2}>
              <Alert intent="success" variant="filled" description="Your email has been verified" />
              <Button
                size="lg"
                variant="primary"
                onClick={navigateToSignIn}
                endDecorator={<ChevronRightIcon sx={{ fontSize: "1.2rem" }} />}
              >
                Continue to sign in
              </Button>
            </Stack>
          ) : (
            <>
              <Stack rowGap={3} mb={4}>
                <Stack flexDirection="column">
                  <Typography level="body-lg">Enter the code sent to</Typography>
                  {!!email && (
                    <Typography level="body-lg" bold>
                      {email}
                    </Typography>
                  )}
                </Stack>
                <FormControl error={!!formik.errors.code}>
                  <PinInput
                    intent={formik.errors.code ? "danger" : undefined}
                    length={6}
                    onChange={(value) => {
                      formik.setFieldValue("code", value);
                      if (formik.errors.code) {
                        formik.setFieldError("code", undefined);
                      }
                    }}
                    onComplete={(value) => {
                      formik.setFieldValue("code", value.split(""));
                      if (formik.errors.code) {
                        formik.setFieldError("code", undefined);
                      }
                      verifyEmail(userId, value);
                    }}
                    autoFocus
                    size="lg"
                    value={formik.values.code}
                    gap={2.5}
                  />
                  {formik.errors.code ? (
                    <FormHelperText>
                      <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                      {formik.errors.code as string}
                    </FormHelperText>
                  ) : null}
                </FormControl>
              </Stack>
              <Button
                size="lg"
                variant="primary"
                loading={isLoading}
                endDecorator={<ChevronRightIcon sx={{ fontSize: "1.2rem" }} />}
                type="submit"
              >
                Verify email
              </Button>
            </>
          )}
        </Stack>
      </AuthCard>
    </>
  );
};

export { AuthVerifyEmail };
